import React, { useState, useEffect } from 'react';
import { FileText, Download, X, Maximize2, Minimize2, Calendar, User, BookOpen, Clock, BarChart2, PieChart, Activity, MapPin, Monitor } from 'lucide-react';
import api from '../api';

const ClassReportsModal = ({ onClose, role, userEmail, subjectId = null, subjectName = null }) => {
  const [selectedDiploId, setSelectedDiploId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [diplomaturas, setDiplomaturas] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const res = await api.get('diplomas/');
      setDiplomaturas(res.data);
      if (res.data.length > 0) {
        setSelectedDiploId(res.data[0].id.toString());
      } else if (role === 'DO' || role === 'PR') {
        // Teachers might not have "diplomas" but have classes
        fetchStats();
      }
    } catch (err) {
      console.error('Error fetching diplomas:', err);
    }
  };

  useEffect(() => {
    if (selectedDiploId || subjectId) {
      fetchStats(selectedDiploId, selectedSubjectId);
    }
  }, [selectedDiploId, selectedSubjectId]);

  const fetchStats = async (diploId = null, subId = null) => {
    setLoading(true);
    try {
      const params = {};
      if (subjectId) {
        params.subject = subjectId;
      } else if (subId && subId !== 'all') {
        params.subject = subId;
      } else if (diploId) {
        params.diploma = diploId;
      }
      
      if (role === 'DO' || role === 'PR') params.teacher_email = userEmail;
      
      // Robust date parsing helper
      const parseDateTime = (dateStr, timeStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const [hours, minutes] = timeStr.split(':').map(Number);
        return new Date(year, month - 1, day, hours, minutes);
      };

      const res = await api.get('classes/', { params });
      const classes = res.data;
      const now = new Date();

      const pastClasses = classes.filter(c => {
        const start = parseDateTime(c.date, c.start_time);
        return start < now;
      });
      const futureClasses = classes.filter(c => {
        const start = parseDateTime(c.date, c.start_time);
        return start >= now;
      });

      const getModalityStats = (classList) => {
        if (!classList || classList.length === 0) return { total: "0.0", vi: "0.0", pr: "0.0", viP: 0, prP: 0 };
        
        let totalHours = 0;
        let viHours = 0;
        let prHours = 0;

        classList.forEach(c => {
          const start = parseDateTime(c.date, c.start_time);
          const end = parseDateTime(c.date, c.end_time);
          let duration = (end - start) / (1000 * 60 * 60);
          
          if (duration < 0) duration = 0;
          
          totalHours += duration;
          if (c.modality === 'PR') {
            prHours += duration;
          } else {
            viHours += duration;
          }
        });

        return {
          total: totalHours.toFixed(1),
          vi: viHours.toFixed(1),
          pr: prHours.toFixed(1),
          viP: totalHours > 0 ? Math.round((viHours / totalHours) * 100) : 0,
          prP: totalHours > 0 ? Math.round((prHours / totalHours) * 100) : 0
        };
      };

      const getDetailedStats = (classList) => {
        const months = {};
        const subjects = {};
        
        classList.forEach(c => {
          const start = parseDateTime(c.date, c.start_time);
          const end = parseDateTime(c.date, c.end_time);
          const duration = Math.max(0, (end - start) / (1000 * 60 * 60));
          
          // Monthly
          const monthKey = start.toLocaleString('es-AR', { month: 'long', year: 'numeric' });
          if (!months[monthKey]) {
            months[monthKey] = { name: monthKey, total: 0, vi: 0, pr: 0, date: start };
          }
          months[monthKey].total += duration;
          if (c.modality === 'PR') months[monthKey].pr += duration;
          else months[monthKey].vi += duration;
          
          // Subject
          const subName = c.subject_name || "Sin nombre";
          if (!subjects[subName]) {
            subjects[subName] = { name: subName, total: 0, vi: 0, pr: 0 };
          }
          subjects[subName].total += duration;
          if (c.modality === 'PR') subjects[subName].pr += duration;
          else subjects[subName].vi += duration;
        });

        return {
          monthly: Object.values(months).sort((a, b) => a.date - b.date),
          subjects: Object.values(subjects).sort((a, b) => b.total - a.total)
        };
      };

      setReportData({
        past: getModalityStats(pastClasses),
        future: getModalityStats(futureClasses),
        total: getModalityStats(classes),
        pastDetails: getDetailedStats(pastClasses),
        allDetails: getDetailedStats(classes)
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatBox = ({ title, stats, icon }) => (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-primary border-opacity-10">
      <div className="d-flex align-items-center gap-2 mb-3 text-primary">
        {icon}
        <h6 className="mb-0 fw-bold">{title}</h6>
      </div>
      <div className="h3 mb-3 fw-bold">{stats.total} <span className="text-muted fs-6 fw-normal">horas</span></div>
      
      <div className="d-flex flex-column gap-2">
        <div>
          <div className="d-flex justify-content-between mb-1 small fw-bold">
            <span className="d-flex align-items-center gap-1"><Monitor size={14} className="text-info"/> Virtuales</span>
            <span>{stats.vi} ({stats.viP}%)</span>
          </div>
          <div className="progress" style={{ height: '8px' }}>
            <div className="progress-bar bg-info" style={{ width: `${stats.viP}%` }}></div>
          </div>
        </div>
        
        <div>
          <div className="d-flex justify-content-between mb-1 small fw-bold">
            <span className="d-flex align-items-center gap-1"><MapPin size={14} className="text-warning"/> Presenciales</span>
            <span>{stats.pr} ({stats.prP}%)</span>
          </div>
          <div className="progress" style={{ height: '8px' }}>
            <div className="progress-bar bg-warning" style={{ width: `${stats.prP}%` }}></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className={`modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable ${isFullscreen ? 'modal-fullscreen' : ''}`}>
        <div className="modal-content glassmorphism">
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
            <h5 className="modal-title section-title fs-4 mb-0 d-flex align-items-center gap-2">
              <BarChart2 /> {subjectName ? `Análisis de Asistencia: ${subjectName}` : 'Reporte de Distribución de Clases'}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn-glass p-1 d-flex align-items-center justify-content-center"
                onClick={() => setIsFullscreen(f => !f)}
                title={isFullscreen ? 'Contraer' : 'Ampliar a pantalla completa'}
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                type="button"
                className="btn-close-premium"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="modal-body p-4">
            {diplomaturas.length > 1 && !subjectId && (
              <div className="row g-3 mb-4">
                <div className="col-md-6 text-start">
                  <label className="form-label fw-bold small text-muted">DIPLOMATURA</label>
                  <select 
                    className="form-select" 
                    value={selectedDiploId} 
                    onChange={e => {
                      setSelectedDiploId(e.target.value);
                      setSelectedSubjectId('all');
                    }}
                  >
                    {diplomaturas.map(d => <option key={d.id} value={d.id}>{d.name} ({d.municipio})</option>)}
                  </select>
                </div>
                <div className="col-md-6 text-start">
                  <label className="form-label fw-bold small text-muted">MATERIA</label>
                  <select 
                    className="form-select" 
                    value={selectedSubjectId} 
                    onChange={e => setSelectedSubjectId(e.target.value)}
                  >
                    <option value="all">Todas las materias</option>
                    {diplomaturas.find(d => d.id.toString() === selectedDiploId)?.subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {diplomaturas.length === 1 && !subjectId && (
              <div className="alert alert-primary bg-opacity-10 py-2 small mb-4 border-0">
                <Activity size={14} className="me-2"/>
                Reporte de: <strong>{diplomaturas[0].name} ({diplomaturas[0].municipio})</strong>
              </div>
            )}

            {(role === 'DO' || role === 'PR') && (
               <div className="alert alert-info py-2 small mb-4">
                  Reporte personalizado para docente: <strong>{userEmail}</strong>
               </div>
            )}


            {loading ? (
              <div className="text-center py-5">
                <Activity className="animate-spin text-primary mb-2" size={32} />
                <p>Calculando estadísticas...</p>
              </div>
            ) : reportData ? (
              <div className="row g-4 text-start">
                <div className="col-md-6 text-start">
                  <StatBox title="Clases Impartidas" stats={reportData.past} icon={<Calendar className="text-primary"/>} />
                </div>
                <div className="col-md-6 text-start">
                  <StatBox title="Clases Programadas" stats={reportData.future} icon={<Calendar className="text-success"/>} />
                </div>
                
                <div className="col-12 mt-4">
                    <div className="alert bg-primary bg-opacity-10 border-primary border-opacity-25 d-flex align-items-center gap-3">
                        <PieChart className="text-primary" size={32} />
                        <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold text-primary">Análisis de Planificación</h6>
                            {reportData.total.total > 0 ? (
                              <>
                                <p className="mb-1 small text-dark opacity-75">
                                    Distribución total ({reportData.total.total} horas):
                                    <strong> {reportData.total.prP}% Presencial</strong> | <strong>{reportData.total.viP}% Virtual</strong>
                                </p>
                                <div className="d-flex gap-2 mt-1">
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between small mb-1">
                                      <span>Presencial</span><span>{reportData.total.pr}</span>
                                    </div>
                                    <div className="progress" style={{height: '6px'}}>
                                      <div className="progress-bar bg-warning" style={{width: `${reportData.total.prP}%`}} />
                                    </div>
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between small mb-1">
                                      <span>Virtual</span><span>{reportData.total.vi}</span>
                                    </div>
                                    <div className="progress" style={{height: '6px'}}>
                                      <div className="progress-bar bg-info" style={{width: `${reportData.total.viP}%`}} />
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <p className="mb-0 small text-dark opacity-75">No hay datos suficientes para analizar.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Monthly Breakdown Table */}
                {reportData.allDetails.monthly && reportData.allDetails.monthly.length > 0 && (
                  <div className="col-12 mt-2 text-start">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-primary border-opacity-10">
                      <div className="d-flex align-items-center gap-2 mb-3 text-primary">
                        <Clock size={20} />
                        <h6 className="mb-0 fw-bold">Distribución de Horas por Mes (Total)</h6>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr className="small">
                              <th className="border-0">Mes</th>
                              <th className="border-0 text-center">Virtual</th>
                              <th className="border-0 text-center">Presencial</th>
                              <th className="border-0 text-end">Total Horas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.allDetails.monthly.map((m, idx) => (
                              <tr key={idx} className="small">
                                <td className="fw-bold text-capitalize">{m.name}</td>
                                <td className="text-center text-info">{m.vi.toFixed(1)}</td>
                                <td className="text-center text-warning">{m.pr.toFixed(1)}</td>
                                <td className="text-end fw-bold">{m.total.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Subject Breakdown Table */}
                {reportData.allDetails.subjects && reportData.allDetails.subjects.length > 0 && (
                  <div className="col-12 mt-4 text-start">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-primary border-opacity-10">
                      <div className="d-flex align-items-center gap-2 mb-3 text-primary">
                        <BookOpen size={20} />
                        <h6 className="mb-0 fw-bold">Distribución de Horas por Materia (Total)</h6>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr className="small">
                              <th className="border-0">Materia</th>
                              <th className="border-0 text-center">Virtual</th>
                              <th className="border-0 text-center">Presencial</th>
                              <th className="border-0 text-end">Total Horas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.allDetails.subjects.map((s, idx) => (
                              <tr key={idx} className="small">
                                <td className="fw-bold">{s.name}</td>
                                <td className="text-center text-info">{s.vi.toFixed(1)}</td>
                                <td className="text-center text-warning">{s.pr.toFixed(1)}</td>
                                <td className="text-end fw-bold">{s.total.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-5 text-muted fst-italic">
                No hay datos disponibles para generar el reporte.
              </div>
            )}
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-secondary w-100" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassReportsModal;
